import {
  Component, Input, OnDestroy,
  ChangeDetectorRef, ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';

import { Subject ,  Subscription } from 'rxjs';

import { BrandService } from 'shared/brand/brand.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { BrandInfoService } from 'shared/brand/brand-info.service';
import {debounceTime, distinctUntilChanged, takeWhile} from 'rxjs/operators';
import { isRetina } from 'utils/media';


@Component({
  moduleId: module.id,
  selector: 'receiver-details-tab',
  templateUrl: 'receiver-details.component.html',
  styleUrls: ['receiver-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReceiverDetailsTabComponent implements OnDestroy {
  readonly isHighDpi = isRetina();

  form: UntypedFormGroup;
  dnsControlName: string = 'doNotSell';
  isActive: boolean = true;

  @Input() receiver: any;

  private receiverChangedSubj = new Subject<any>();
  receiverChanged$ = this.receiverChangedSubj.asObservable();

  private formChangesSubscr: Subscription;

  tooltipBodyHtml: string = `
    <p class='tooltip-text'>Request information to be updated here:
      <a class='link-dashed' href='https://goo.gl/forms/EbPYmKdMmcPUr56u2' target='_blank'>Receiver update form</a>
    </p>
  `;

  statusTooltipHtml: string = `
    <h1 class='tooltip-header'>Receiver status</h1>
    ${this.tooltipBodyHtml}
  `;

  primaryContactTooltipHtml: string = `
    <h1 class='tooltip-header'>Update information</h1>
    ${this.tooltipBodyHtml}
  `;

  constructor(
    // private dynamicComponentLoader: DynamicComponentLoader,
    private fb: UntypedFormBuilder,
    private brandService: BrandService,
    private permissionsService: PermissionsService,
    private brandInfoService: BrandInfoService,
    private ref: ChangeDetectorRef,
  ) {
    this.form = this.fb.group({[this.dnsControlName]:  this.fb.control('')});
  }

  get receiveStatus(): string {
    if (!this.receiver) { return null; }
    return _.get(this.receiver, 'ready_to_receive') === 1 ?
      'Ready to receive data' :
      'Pending';
  }

  get receiverAddress(): string {
    const addressArray = [];

    if (this.receiver.street_address) { addressArray.push(this.receiver.street_address); }
    if (this.receiver.city) { addressArray.push(this.receiver.city); }
    if (this.receiver.state) { addressArray.push(this.receiver.state); }
    if (this.receiver.zip_code) { addressArray.push(this.receiver.zip_code); }
    if (this.receiver.country) { addressArray.push(this.receiver.country); }

    return _.join(addressArray, ', ');
  }

  get googleMapsLink() {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.receiverAddress)}`;
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnDestroy() {
    this.isActive = false;
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public setData(receiver: any) {
    this.receiver = receiver;
    this.unsubscribeFormChanges();
    this.updateForm();
    this.subscribeFormChanges();
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected hasPermission(allow: Array<string>|string, requireAll: boolean= false) {
    return this.permissionsService.hasPermission(allow, requireAll);
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private updateForm() {
    this.form.get(this.dnsControlName).reset(this.receiver.do_not_sell, {
      onlySelf: true,
      emitEvent: false,
    });
  }

  private subscribeFormChanges() {
    this.formChangesSubscr = this.form.get(this.dnsControlName)
      .valueChanges
      .pipe(
          distinctUntilChanged(),
          debounceTime(500),
          takeWhile(() => this.isActive)
      )
      .subscribe((event) => {
        const doNotCell = Number(event);
        const receiverId = _.clone(this.receiver._id);
        this.brandInfoService.putReceiverDNS(this.brandService.getBrand(), {
          receiverId,
          [this.dnsControlName]: doNotCell,
        })
          .pipe(takeWhile(() => this.isActive))
          .subscribe((res: any) => {
            if (_.get(res, 'success')) {
              this.receiverChangedSubj.next({receiverId, doNotCell});
            } else {
              // rollback value if unsuccessful
              this.form.get(this.dnsControlName).setValue(this.receiver.do_not_sell);
            }
          });
      });
  }

  private unsubscribeFormChanges() {
    if (this.formChangesSubscr) { this.formChangesSubscr.unsubscribe(); }
  }
}
